'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { UTM_SESSION_STORAGE_KEY } from '@/constants';
import {
  clearExistingSessionParams,
  saveParamsToSession,
  getSessionParams,
} from '@/lib/sessionStorage';
import { urlHasUTMParams } from '@/lib/utmParams';

export interface SessionContextProps {
  utmParamsString: string;
  setUTMParamsString: Dispatch<SetStateAction<string>>;
}

export const SessionContext = createContext<SessionContextProps | undefined>(
  undefined,
);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [utmParamsString, setUTMParamsString] = useState('');

  useEffect(() => {
    const { location } = window;

    const paramKeysToExclude = [
      'origin',
      'list',
      '_c_reason',
      '_c_email',
      '_c_list',
    ];
    const allParamsFromURL = new URLSearchParams(location.search);
    allParamsFromURL.forEach((value, key) => {
      if (paramKeysToExclude.includes(key)) {
        allParamsFromURL.delete(key);
      }
    });
    const urlParamsString = allParamsFromURL.toString();
    const urlHasUTM = urlHasUTMParams(allParamsFromURL);
    const currentSessionParams = getSessionParams(UTM_SESSION_STORAGE_KEY);

    // If the URL contains utm_ params, replace all session storage with what's in the URL
    if (urlHasUTM) {
      clearExistingSessionParams(UTM_SESSION_STORAGE_KEY);
      saveParamsToSession(allParamsFromURL, UTM_SESSION_STORAGE_KEY);
      setUTMParamsString(urlParamsString);
    }
    // If the URL doesn't have params, but session does from a previous URL, set that to state on page load
    else if (currentSessionParams) {
      setUTMParamsString(currentSessionParams);
    }
    // if no URL or session params, do nothing
    else {
    }
  }, []);

  return (
    <SessionContext.Provider
      value={{
        utmParamsString,
        setUTMParamsString,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);

  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
}
